import React, { useState, useEffect, useCallback } from 'react';
import language from '../../utils/LanguageSettings';
import translations from '../../text/get-started-translations.json';
import RenderSnA from '../functions/renderSnA';
import { 
  getPromoCodeFromUrl, 
  getCaptchaImageUrl, 
  refreshCaptchaUrl,
  useCouponValidation
} from '../../utils/couponService';
import { useAuth } from '../../contexts/AuthProvider';
import "../../css/redeem-code.scss";
import phoneCall from "../../images/icon/phone-call.svg";
import whatsApp from "../../images/icon/icon-whatsapp.svg";

const RedeemCode: React.FC = () => {
  const { loading, userData, isLoggedIn, authVerified } = useAuth();
  const [redemptionCode, setRedemptionCode] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const navEntries = performance.getEntriesByType('navigation');
      const isReload = navEntries.length > 0 && (navEntries[0] as PerformanceNavigationTiming).type === 'reload';
      
      if (isReload) {
        sessionStorage.removeItem('redemptionCode');
        return '';
      }
      return sessionStorage.getItem('redemptionCode') || '';
    }
    return '';
  });
  const [captcha, setCaptcha] = useState<string>('');
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [captchaSrc, setCaptchaSrc] = useState<string>(getCaptchaImageUrl());

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('redemptionCode', redemptionCode);
    }
  }, [redemptionCode]);

  const {
    redemptionCodeError,
    captchaError,
    isSubmitting,
    setIsSubmitting,
    validateRedemptionCode,
    validateCouponCode,
    clearErrors,
    setRedemptionCodeError
  } = useCouponValidation(language);

  useEffect(() => {
    const promoCode = getPromoCodeFromUrl();
    if (promoCode) {
      // If the url has parameter promocode, redirect to SSP directly using POST for security
      window.location.href= "https://ssp.nowtv.now.com/choose-plan?coupon="+promoCode+"&lang="+language; 
      return;
    }
  }, []);

  const validateInputs = useCallback(() => {
    if (redemptionCode.startsWith('DV')) {
      validateRedemptionCode(redemptionCode);
      setIsButtonDisabled(true);
    } else if (redemptionCode.trim() !== '' && captcha.trim() !== '') {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [redemptionCode, captcha, validateRedemptionCode, clearErrors]);

  const refreshCaptcha = useCallback(() => {
    const newCaptchaUrl = refreshCaptchaUrl(captchaSrc);
    setCaptchaSrc(newCaptchaUrl);
    setCaptcha('');
    setIsButtonDisabled(true);
  }, [captchaSrc]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsButtonDisabled(true);
    
    const isCodeValid = validateRedemptionCode(redemptionCode);
    if (!isCodeValid) {
      setIsSubmitting(false);
      return;
    }

    await validateCouponCode(redemptionCode, captcha, refreshCaptcha, language);
  }, [redemptionCode, captcha, validateRedemptionCode, validateCouponCode, refreshCaptcha, setIsSubmitting]);

  useEffect(() => {
    const handleInput = (e: Event) => {
      validateInputs();
    };

    const redemptionCodeInput = document.getElementById('redemption-code');
    const captchaInput = document.getElementById('captcha');

    if (redemptionCodeInput) {
      redemptionCodeInput.addEventListener('input', handleInput);
    }
    if (captchaInput) {
      captchaInput.addEventListener('input', handleInput);
    }

    return () => {
      if (redemptionCodeInput) {
        redemptionCodeInput.removeEventListener('input', handleInput);
      }
      if (captchaInput) {
        captchaInput.removeEventListener('input', handleInput);
      }
    };
  }, [validateInputs]);

  useEffect(() => {
    validateInputs();
  }, [redemptionCode, captcha, validateInputs]);

  const hasPromoCode = !!getPromoCodeFromUrl();
  if (loading || !authVerified || hasPromoCode) {
      return (
          <div style={{ minHeight: '80vh' }}>
              <div id="loader">
                  <div className="spinner-border"></div>
              </div>
          </div>
      );
  }

  const pageTranslations = translations[language];
  const fsaType = userData?.fsaType || '';
  const novaXIndicator = userData?.novaXIndicator;
  
  const seo: any = {
    Title: language === "en" ? "Redeem Code" : "代碼換領"
  };
  const ga: any = {};

  const handleRedeemCodeChange = (e: any) => {
    setRedemptionCode(e.target.value);
    setRedemptionCodeError("");
  }

  return (
    <>
      <RenderSnA seo={seo} ga={ga} />
      <section id="main-content" className='redeem-code'>
        <div className="container-fluid">
          <div className="page-wrapper">
            {(isLoggedIn && fsaType === "TV" && novaXIndicator === false) ? (
              <div className="container">
                <div className="invalid-user">
                  <div className="heading">
                    <h2>{pageTranslations.redeemCodeNotOTTHeading}</h2>
                  </div>
                  <div className="message">
                    <p>
                      {pageTranslations.redeemCodeNotOTTMessage}
                      <a href="tel:1833888">1833 888</a>
                      {pageTranslations.redeemCodeNotOTTMessage1}
                    </p>
                    <p>{pageTranslations.redeemCodeNotOTTMessage2}</p>
                    <p>{pageTranslations.redeemCodeNotOTTMessage3}</p>
                  </div>
                </div>
                <div className="need-help-wrapper">
                  <h2>{pageTranslations.redeemCodeNotOTTWrapperHead}</h2>
                  <div className="container">
                    <div className="options-wrap">
                      <div className="item">
                        <a href="tel:1833888">
                          <div className="img-wrap">
                            <img 
                              src={phoneCall} 
                              alt="Phone support" 
                              className="phone" 
                            />
                          </div>
                          <h4>1833 888</h4>
                        </a>
                      </div>

                      <div className="item">
                        <a href="https://api.whatsapp.com/send?phone=85260551888" target="_blank" rel="noopener noreferrer">
                          <div className="img-wrap">
                            <img 
                              src={whatsApp} 
                              alt="WhatsApp support" 
                              className="whatsapp" 
                            />
                          </div>
                          <h4>{pageTranslations.redeemCodeNotOTTWrapperContent1}</h4>
                        </a>
                      </div>

                      <div className="item">
                        <a href="https://api.whatsapp.com/send?phone=85296133533" target="_blank" rel="noopener noreferrer">
                          <div className="img-wrap">
                            <img 
                              src={whatsApp} 
                              alt="WhatsApp support" 
                              className="whatsapp" 
                            />
                          </div>
                          <h4>{pageTranslations.redeemCodeNotOTTWrapperContent2}</h4>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="container">
                <div className="valid-user">
                  <div className="heading">
                    <h2>{pageTranslations.redeemCodeOTTHeading}</h2>
                  </div>

                  <div className="redemption-wrapper">
                    {userData?.cust_type === 'r2stb' ? (
                        <h4>{pageTranslations.redeemCodeTVWrapper}</h4>
                    ) : (
                        <h4>{pageTranslations.redeemCodeOTTWrapper}</h4>
                    )}
                    
                    <form onSubmit={handleSubmit}>
                      <div className="box">
                        <input
                          type="text"
                          required
                          id="redemption-code"
                          value={redemptionCode}
                          onChange={(e: any) => handleRedeemCodeChange(e)}
                        />
                        <label htmlFor="redemption-code">{pageTranslations.redeemCodeOTTLabel1}</label>
                        {userData?.cust_type === 'r2stb' ? null : (
                          <h4>
                              {pageTranslations.redeemCodeOTTMessage1}
                              <a href="https://www.nowtv.now.com/support/how-to-redeem-code" target="_blank" rel="noopener noreferrer">
                                  {pageTranslations["redeemCodeOTTMessage1-1"]}
                               </a>
                          </h4>
                        )}
                        {redemptionCodeError && (
                          <p className="error" dangerouslySetInnerHTML={{ __html: redemptionCodeError }} style={{ display: redemptionCodeError ? 'block' : 'none' }}></p>
                        )}
                      </div>

                      <div className="recaptcha">
                        <img 
                          src={captchaSrc} 
                          alt="Captcha verification image"
                        />
                        <button
                          type="button"
                          onClick={refreshCaptcha}
                          disabled={isSubmitting}
                        >
                          {pageTranslations.redeemCodeOTTRefersh}
                        </button>
                      </div>

                      <div className="box">
                        <input
                          type="text"
                          required
                          id="captcha"
                          value={captcha}
                          onChange={(e) => setCaptcha(e.target.value)}
                        />
                        <label htmlFor="captcha">{pageTranslations.redeemCodeOTTLabel2}</label>
                        {captchaError && (
                          <p className="error" style={{ display: captchaError ? 'block' : 'none' }}>{captchaError}</p>
                        )}
                      </div>

                      <div className="button-cta">
                        <button
                          type="submit"
                          className="btn primary"
                          disabled={isButtonDisabled || isSubmitting}
                          aria-disabled={isButtonDisabled || isSubmitting}
                        >
                          {pageTranslations.redeemCodeOTTContinue}
                        </button>
                        {userData?.cust_type === 'not-binded' && (
                          <h4>
                            {pageTranslations.redeemCodeGuestMessage1}
                            <a href={`https://tvbinding.now.com/verifynowidpa/${language === 'zh' ? 'zh' : 'en'}/index.jsp?externalBrowser=true#open`} target="_blank" rel="noopener noreferrer">
                                {pageTranslations["redeemCodeGuestMessage1-1"]}
                            </a>
                          </h4>
                        )}
                      </div>
                    </form>

                    <p className="note">
                      {pageTranslations.redeemCodeOTTNote}
                      <a 
                        href={`https://policies.google.com/privacy?hl=${language === "en" ? "en" : "zh-HK"}#open`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="link"
                      >
                        {pageTranslations["redeemCodeOTTNote-1"]}
                      </a>
                      {pageTranslations["redeemCodeOTTNote-2"]}
                      <a 
                        href={`https://policies.google.com/terms?hl=${language === "en" ? "en" : "zh-HK"}#open`} 
                        className="link" 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {pageTranslations["redeemCodeOTTNote-3"]}
                      </a>
                      {pageTranslations["redeemCodeOTTNote-4"]}
                    </p>
                  </div>
                </div>
              </div>           
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default RedeemCode;